import { useMsal } from "@azure/msal-react";
import { useIsAuthenticated } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { createLogger } from "../helper/logger";

const useAccessToken = () => {
	const isFakeAuth =
		process.env.REACT_APP_AUTH_FAKE_IT === "YES" ||
		process.env.REACT_APP_AUTH_FAKE_IT === "TRUE";

	// Use real authentication if not faking it
	const realIsAuthenticated = useIsAuthenticated();
	const { instance, accounts } = useMsal();

	// Override authentication when in fake mode
	const isAuthenticated = isFakeAuth ? true : realIsAuthenticated;

	// Track previous values for change detection
	const prevDepsRef = useRef({
		isFakeAuth,
		isAuthenticated,
		activeAccountId: null,
		instanceId: instance ? Object.getOwnPropertyNames(instance).length : 0,
	});

	// Memoize account for stable reference
	const activeAccount = useMemo(() => accounts[0] || null, [accounts]);
	const logger = useMemo(() => createLogger("useAccessToken"), []);

	// Log changes to dependencies
	useEffect(() => {
		const currentDeps = {
			isFakeAuth,
			isAuthenticated,
			activeAccountId: activeAccount ? activeAccount.homeAccountId : null,
			instanceId: instance ? Object.getOwnPropertyNames(instance).length : 0,
		};

		// Check each dependency for changes
		for (const key in currentDeps) {
			if (currentDeps[key] !== prevDepsRef.current[key]) {
				logger.debug(`Dependency changed: ${key}`, {
					previous: prevDepsRef.current[key],
					current: currentDeps[key],
				});
			}
		}

		// Update reference
		prevDepsRef.current = currentDeps;
	}, [isFakeAuth, isAuthenticated, activeAccount, instance, logger]);

	// Memoize getToken function to avoid triggering re-renders when used as a dependency
	const getToken = useCallback(async () => {
		// Return a fake token when in fake mode
		if (isFakeAuth) {
			return "fake-mock-token-for-development-only";
		}

		if (!isAuthenticated) {
			return undefined;
		}

		if (!activeAccount) {
			console.error("No active account");
			return undefined;
		}

		const request = {
			scopes: [],
			account: activeAccount,
		};
		try {
			const response = await instance.acquireTokenSilent(request);
			return response.idToken;
		} catch (error) {
			logger.error("acquireTokenSilent error", error);
			throw error;
		}
	}, [isFakeAuth, isAuthenticated, activeAccount, instance, logger]);

	return {
		getToken,
		isFakeAuth,
	};
};

export default useAccessToken;
